import { Transition } from '@headlessui/react';
import ToastComponent from '@shared/components/ToastComponent/Toast';
import cx from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import useToast from 'contexts/ToastProvider/useToast';

import { ToastItem } from './types';
import { toastTransformAnimations, placeholderTransformAnimations, TOAST_TTL } from './utils';

const ToastListItem = ({
  id,
  variant,
  icon,
  title,
  description,
  size,
  autoDismiss = true,
  hasCloseButton,
  duration,
  onClose,
  onComplete,
  onAction,
  actionText,
  actionButtonVariant,
  closeButtonTestId,
}: ToastItem) => {
  const { removeToast } = useToast();
  const [show, setShow] = useState(false);

  const initClose = useCallback(() => {
    setShow(false);
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    setShow(true);

    if (!autoDismiss) return;

    const toastTimeout = setTimeout(initClose, TOAST_TTL);

    return () => clearTimeout(toastTimeout);
  }, [autoDismiss, initClose]);

  const deleteToast = () => removeToast(id);

  // Wrap the caller-supplied onAction so clicking the action button also triggers
  // the visual close animation (setShow → Headless UI leave transition → deleteToast).
  // Without this, onAction called removeToast directly, but Headless UI could keep
  // the element alive during its in-progress animation, making the toast appear to
  // linger. Calling initClose() ensures the transition fires regardless.
  const handleAction = useCallback(() => {
    if (onAction) onAction();
    initClose();
  }, [onAction, initClose]);

  return (
    <Transition show={show}>
      <Transition.Child {...placeholderTransformAnimations} afterLeave={deleteToast} className="overflow-y-hidden">
        <Transition.Child {...toastTransformAnimations} className="flex justify-end" data-testid={`toast-${id}`}>
          <ToastComponent
            size={size}
            variant={variant}
            icon={icon}
            description={description}
            className={cx('mt-2', { 'toast-info': variant === 'info' })}
            hasCloseButton={hasCloseButton}
            onClose={initClose}
            onComplete={onComplete}
            duration={duration}
            onAction={handleAction}
            actionText={actionText}
            actionButtonVariant={actionButtonVariant}
            closeButtonTestId={closeButtonTestId}
          >
            {title}
          </ToastComponent>
        </Transition.Child>
      </Transition.Child>
    </Transition>
  );
};

export default ToastListItem;
